/**
 * Clerk Configuration and Authentication Module
 * Reuses spatial-join Clerk instance
 */

import { Clerk } from '@clerk/clerk-js';

// Clerk configuration - reusing spatial-join keys
// Note: Production key only works on spatialjoin.app domain
// For localhost development, you need test keys from Clerk dashboard

// Check if we're in development (localhost)
function isLocalhost() {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
}

// Get the appropriate publishable key
function getPublishableKey() {
    if (isLocalhost()) {
        console.log('Running on localhost - Clerk authentication disabled');
        return null; // Set to 'pk_test_...' if you have test keys
    }
    // TODO: Get Clerk keys for mapdatax.com domain
    // For now, return null to run in anonymous mode everywhere
    return null;
    // return 'pk_live_YOUR_MAPDATAX_KEY_HERE';
}

export const CLERK_CONFIG = {
    publishableKey: getPublishableKey(),

    appearance: {
        baseTheme: 'light',
        variables: {
            colorPrimary: '#2563eb',
            colorBackground: '#ffffff',
            colorText: '#1f2937'
        }
    }
};

// Module-level state
let clerkInstance = null;
let currentUser = null;
let authChangeCallbacks = [];

/**
 * Initialize Clerk SDK
 * @returns {Promise<Clerk>} Clerk instance
 */
export async function initializeClerk() {
    if (clerkInstance) {
        return clerkInstance;
    }

    // Skip Clerk initialization if no publishable key (development mode)
    if (!CLERK_CONFIG.publishableKey) {
        console.log('✓ Clerk disabled - Running in anonymous mode (localhost)');
        return null;
    }

    try {
        console.log('Initializing Clerk with key:', CLERK_CONFIG.publishableKey.substring(0, 15) + '...');

        // Create new Clerk instance
        clerkInstance = new Clerk(CLERK_CONFIG.publishableKey);

        // Load Clerk (waits for SDK to be ready)
        await clerkInstance.load({
            appearance: CLERK_CONFIG.appearance
        });

        // Get current user
        currentUser = clerkInstance.user;

        // Listen for auth state changes
        clerkInstance.addListener((event) => {
            const newUser = event.user;
            if (newUser !== currentUser) {
                currentUser = newUser;
                notifyAuthChange(newUser);
            }
        });

        console.log('✓ Clerk initialized:', currentUser ? `Logged in as ${currentUser.firstName}` : 'Anonymous');

        return clerkInstance;
    } catch (error) {
        console.error('✗ Failed to initialize Clerk:', error.message);
        // Don't throw - just return null and continue in anonymous mode
        return null;
    }
}

/**
 * Get the Clerk instance
 * @returns {Clerk|null}
 */
export function getClerk() {
    return clerkInstance;
}

/**
 * Get the current authenticated user
 * @returns {Object|null} Clerk user object or null if not authenticated
 */
export function getCurrentUser() {
    return currentUser;
}

/**
 * Get the current user's ID, or 'anonymous' if not authenticated
 * @returns {string} User ID or 'anonymous'
 */
export function getUserId() {
    return currentUser?.id || 'anonymous';
}

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export function isAuthenticated() {
    return currentUser !== null;
}

/**
 * Register a callback for auth state changes
 * @param {Function} callback - Function to call when auth state changes
 * @returns {Function} Unsubscribe function
 */
export function onAuthStateChange(callback) {
    authChangeCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
        authChangeCallbacks = authChangeCallbacks.filter(cb => cb !== callback);
    };
}

/**
 * Notify all listeners of auth state change
 * @param {Object|null} user - New user state
 */
function notifyAuthChange(user) {
    authChangeCallbacks.forEach(callback => {
        try {
            callback(user);
        } catch (error) {
            console.error('Error in auth change callback:', error);
        }
    });
}

/**
 * Open sign in modal
 */
export async function openSignIn() {
    if (!clerkInstance) {
        throw new Error('Clerk not initialized');
    }

    await clerkInstance.openSignIn({
        routing: 'virtual',
        appearance: CLERK_CONFIG.appearance
    });
}

/**
 * Open sign up modal
 */
export async function openSignUp() {
    if (!clerkInstance) {
        throw new Error('Clerk not initialized');
    }

    await clerkInstance.openSignUp({
        routing: 'virtual',
        appearance: CLERK_CONFIG.appearance
    });
}

/**
 * Open user profile modal
 */
export async function openUserProfile() {
    if (!clerkInstance) {
        throw new Error('Clerk not initialized');
    }

    await clerkInstance.openUserProfile({
        routing: 'virtual',
        appearance: CLERK_CONFIG.appearance
    });
}

/**
 * Sign out the current user
 */
export async function signOut() {
    if (!clerkInstance) {
        throw new Error('Clerk not initialized');
    }

    await clerkInstance.signOut();
}
